import React from 'react';
import { SCALE_MIN, SCALE_MAX, BAND_MIN } from '../../constants';

// Quadrant view (custom SVG, chosen over amCharts for print determinism, live
// CSS-token theming, and zero async/watermark overhead):
//   - a 45° diagonal where Self == All But Self,
//   - auto-scaled SQUARE axes (padded to the data, not forced to 1..6) so
//     clustered high scores stay readable — kept inside the 1..6 scale and always
//     spanning the favourable threshold (4) so the quadrant division is visible,
//   - a shaded favourable quadrant (All But Self >= 4 AND Self >= 4).

// Square plot box inside the 360x360 viewBox (equal width/height so y == x maps
// to a true 45° line); margins leave room for the axis labels.
const X0 = 48;
const X1 = 344;
const Y0 = 16;
const Y1 = 312;

// Auto-scaled domain: pad to the data, clamp inside 1..6, and always include the
// favourable threshold so the shaded quadrant and threshold lines stay on screen.

// Colours resolve from the .divergence CSS custom properties (they inherit into
// the SVG), so the quadrant tracks the same tokens as the dumbbell view.
const QuadrantChart = (props) => {
    const VALUES = props.divergenceData.flatMap((d) => [d.self, d.others]);
    const RAW_MIN = Math.min(...VALUES);
    const RAW_MAX = Math.max(...VALUES);
    const PAD = Math.max(0.2, (RAW_MAX - RAW_MIN) * 0.12);
    let DOMAIN_MIN = Math.max(SCALE_MIN, Math.min(RAW_MIN - PAD, BAND_MIN));
    let DOMAIN_MAX = Math.min(SCALE_MAX, Math.max(RAW_MAX + PAD, BAND_MIN));
    if (DOMAIN_MAX - DOMAIN_MIN < 1) {
        const mid = (DOMAIN_MIN + DOMAIN_MAX) / 2;
        DOMAIN_MIN = Math.max(SCALE_MIN, mid - 0.5);
        DOMAIN_MAX = Math.min(SCALE_MAX, mid + 0.5);
    }

    const xFor = (v) => X0 + ((v - DOMAIN_MIN) / (DOMAIN_MAX - DOMAIN_MIN)) * (X1 - X0);
    const yFor = (v) => Y1 - ((v - DOMAIN_MIN) / (DOMAIN_MAX - DOMAIN_MIN)) * (Y1 - Y0);

    // Always six evenly-spaced tick labels across the auto-scaled domain (one decimal).
    const TICK_COUNT = 6;
    const TICKS = Array.from(
        { length: TICK_COUNT },
        (_, i) => DOMAIN_MIN + (i / (TICK_COUNT - 1)) * (DOMAIN_MAX - DOMAIN_MIN)
    );

    const fx = xFor(BAND_MIN);
    const fy = yFor(BAND_MIN);
    const midY = (Y0 + Y1) / 2;
    return (
        <svg
            viewBox='0 0 360 360'
            width='400'
            height='400'
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            role='img'
            aria-label='Self vs All But Self quadrant'
        >
            {/* Favourable zone (upper-right): All But Self >= 4 and Self >= 4 */}
            <rect x={fx} y={Y0} width={X1 - fx} height={fy - Y0} fill='color-mix(in oklch, oklch(58% .09 190) 10%, transparent)' />

            {/* Over-rating zone (upper-left): Self >= 4 but All But Self < 4 */}
            <rect x={X0} y={Y0} width={fx - X0} height={fy - Y0} fill='color-mix(in oklch, oklch(76% .14 55) 8%, transparent)' />

            {/* Axes */}
            <line x1={X0} y1={Y0} x2={X0} y2={Y1} stroke='var(--dv-connector)' />
            <line x1={X0} y1={Y1} x2={X1} y2={Y1} stroke='var(--dv-connector)' />

            {/* Diagonal where Self == All But Self (true 45° on the square plot) */}
            <line
                x1={xFor(DOMAIN_MIN)}
                y1={yFor(DOMAIN_MIN)}
                x2={xFor(DOMAIN_MAX)}
                y2={yFor(DOMAIN_MAX)}
                stroke='var(--dv-connector)'
                strokeDasharray='4 4'
            />

            {/* Favourable threshold lines (value 4) */}
            <line x1={fx} y1={Y0} x2={fx} y2={Y1} stroke='var(--dv-accent)' strokeOpacity='0.35' strokeDasharray='2 3' />
            <line x1={X0} y1={fy} x2={X1} y2={fy} stroke='var(--dv-accent)' strokeOpacity='0.35' strokeDasharray='2 3' />

            {/* Tick labels along the auto-scaled axes */}
            {TICKS.map((t) => (
                <text key={`x${t}`} x={xFor(t)} y={Y1 + 14} textAnchor='middle' fill='#5b646f' fontSize='9'>
                    {t.toFixed(1)}
                </text>
            ))}
            {TICKS.map((t) => (
                <text key={`y${t}`} x={X0 - 8} y={yFor(t) + 3} textAnchor='end' fill='#5b646f' fontSize='9'>
                    {t.toFixed(1)}
                </text>
            ))}

            {/* One marker per category: orange when Self over-rates, else blue */}
            {props.divergenceData.map((row) => {
                const overRates = row.self > row.others;
                return (
                    <circle
                        key={row.category}
                        cx={xFor(row.others)}
                        cy={yFor(row.self)}
                        r='5.4'
                        fill={overRates ? 'var(--dv-self)' : 'var(--dv-others)'}
                        stroke='#ffffff'
                        strokeWidth='1.5'
                    >
                        <title>
                            {`${row.category} — Self ${row.self.toFixed(2)}, Others ${row.others.toFixed(2)}`}
                        </title>
                    </circle>
                );
            })}

            {/* Axis titles */}
            <text x={(X0 + X1) / 2} y='348' textAnchor='middle' fill='#5b646f' fontSize='11' fontWeight='500'>
                All But Self →
            </text>
            <text
                x='12'
                y={midY}
                textAnchor='middle'
                transform={`rotate(-90 12 ${midY})`}
                fill='#5b646f'
                fontSize='10'
                fontWeight='500'
            >
                Self →
            </text>
        </svg>
    );
};

export default QuadrantChart;
